<template>
  <Form ref="dynamicFormRef" :model="formData" :rules="rules" class="dynamic-form" :label-width="labelWidth">
    <FormItem 
      v-for="item in formConfig" 
      :key="item.key"
      :label="lang === 'zh' ? item.name : item.nameEN"
      :prop="item.key"
    >
      <!-- 下拉框 -->
      <Select 
        v-if="item.type === 'select'"
        v-model="formData[item.key]"
        :placeholder="lang === 'zh' ? '请选择' + item.name : 'Please select ' + item.nameEN"
        clearable
        @on-clear="() => handleSelectClear(item)"
      >
        <Option 
          v-for="opt in item.options" 
          :key="opt.id" 
          :value="opt.id"
        >
          {{ lang === 'zh' ? opt.name : opt.nameEN }}
        </Option>
      </Select>

      <!-- 数字输入框 - 只限制最小值为0 -->
      <template v-else-if="item.type === 'number'">
        <div style="display: flex; align-items: center;">
          <InputNumber 
            v-model="formData[item.key]"
            :placeholder="lang === 'zh' ? '请输入' + item.name : 'Please input ' + item.nameEN"
            style="flex: 1;"
            :min="0"
            @on-blur="(e) => handleNumberBlur(e, item)"
          />
          <span v-if="item.unit" style="margin-left: 8px;">{{ item.unit }}</span>
        </div>
      </template>

      <!-- 数字输入框 - 替代方案2 -->
      <Input 
        v-else-if="item.type === 'number'"
        v-model="formData[item.key]"
        type="number"
        :placeholder="lang === 'zh' ? '请输入' + item.name : 'Please input ' + item.nameEN"
      >
        <template v-if="item.unit" slot="append">{{ item.unit }}</template>
      </Input>

      <!-- 文本框 -->
      <Input 
        v-else-if="item.type === 'text'"
        v-model="formData[item.key]"
        :placeholder="lang === 'zh' ? '请输入' + item.name : 'Please input ' + item.nameEN"
      />

      <!-- 文本域 -->
      <Input 
        v-else-if="item.type === 'textarea'"
        v-model="formData[item.key]"
        type="textarea"
        :placeholder="lang === 'zh' ? '请输入' + item.name : 'Please input ' + item.nameEN"
      />
    </FormItem>
  </Form>
</template>

<script>
import { mapState } from 'vuex';
import api from "@/api";

export default {
  name: 'DynamicForm',
  props: {
    // 配置键名
    configKey: {
      type: String,
      default: ''
    },
    // 表单数据
    value: {
      type: [Object, String],
      default: () => ({})
    },
    // 标签宽度
    labelWidth: {
      type: Number,
      default: 120
    }
  },
  data() {
    return {
      formData: {},
      formConfig: [],
      loading: false,
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language
    }),
    // 生成校验规则
    rules() {
      const rules = {};
      this.formConfig.forEach(item => {
        const itemRules = [];
        const rulesObj = this.getFieldRules(item)
        for(const key in rulesObj){
          const val = rulesObj[key]['value']
           switch(key){
            case 'required':
              itemRules.push({
                required: val,
                message: this.lang === 'zh' ? (rulesObj[key]['message'] || `请输入${item.name}`) : (rulesObj[key]['messageEN'] || `Please input ${item.nameEN}`),
                trigger: 'blur'
              })
              break;
            case 'min':
              itemRules.push({
                type: 'number',
                min: Number(val),
                message: this.lang === 'zh' ? (rulesObj[key]['message'] || `${item.name}不能小于${val}`) : (rulesObj[key]['messageEN'] || `${item.nameEN} cannot be less than ${val}`),
                trigger: 'blur',
                validator: (rule, value, callback) => {
                  // 如果值为空且不是必填项，则跳过验证
                  if (value === '' || value === null || value === undefined) {
                    callback();
                  } else if (value !== '' && value !== null && value !== undefined && Number(value) < Number(val)) {
                    callback(new Error(rule.message));
                  } else {
                    callback();
                  }
                }
              })
              break;
            case 'max':
              itemRules.push({
                type: 'number',
                max: Number(val),
                message: rulesObj[key]['message'] ? rulesObj[key]['message'] : this.lang === 'zh' ? `${item.name}不能大于${val}` : `${item.nameEN} cannot be greater than ${val}`,
                trigger: 'blur',
                validator: (rule, value, callback) => {
                  // 如果值为空且不是必填项，则跳过验证
                  if (value === '' || value === null || value === undefined) {
                    callback();
                  } else if (value !== '' && value !== null && value !== undefined && Number(value) > Number(val)) {
                    callback(new Error(rule.message));
                  } else {
                    callback();
                  }
                }
              })
              break;
           } 
        }
        if (itemRules.length > 0) {
          rules[item.key] = itemRules;
        }
      });
      return rules;
    }
  },
  watch: {
    configKey: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchConfig();
        }
      }
    },
    value: {
      immediate: true,
      handler(val) {
          // 处理字符串类型的值
          let parsedValue = val ? JSON.parse(JSON.stringify(val)) : {};
          this.formConfig.forEach(item=>{
            const dataVal = parsedValue[item.key]
            if(item.type === 'number'){
              parsedValue[item.key] = dataVal ? Number(dataVal) : null
            }else{
              parsedValue[item.key] = dataVal || ''
            }
          });
          this.formData = parsedValue
      }
    }
  },
  methods: {
    // 获取字段的验证规则
    getFieldRules(item) {
      const rules = {}
      if(item.rules && Array.isArray(item.rules)){
        item.rules.forEach(jb=>{
          for(let key in jb){
            if(key !== 'message'){
                rules[key] = {
                value: jb[key],
                message: jb['message'] ? this.lang === 'zh' ? jb['message'] : jb['messageEN'] : '',
              }
            }
          }
        })
      }
      return rules
    },
    // 判断字段是否必填
    isFieldRequired(item) {
      if (item.rules && Array.isArray(item.rules)) {
        for (const rule of item.rules) {
          if (rule.required === true) {
            return true;
          }
        }
      }
      return false;
    },
    // 获取最小值 - 优先从rules中获取
    getMinValue(item) {
      // 首先检查rules中是否有min
      if (item.rules && Array.isArray(item.rules)) {
        for (const rule of item.rules) {
          if (rule.min !== undefined) {
            return Number(rule.min);
          }
        }
      }
      // 默认返回0
      return 0;
    },
    
    // 获取最大值 - 优先从rules中获取
    getMaxValue(item) {
      // 首先检查rules中是否有max
      if (item.rules && Array.isArray(item.rules)) {
        for (const rule of item.rules) {
          if (rule.max !== undefined) {
            return Number(rule.max);
          }
        }
      }
      return undefined;
    },
    // 处理数字输入失去焦点事件
    handleNumberBlur(event, item) {
      const val = this.formData[item.key];
      if (!val) return;
      
      // 只确保数值不小于0
      if (val < 0) {
        this.formData[item.key] = 0;
      }
    },
    // 处理数据类型转换
    processDataTypes(data) {
      if (!this.formConfig || !this.formConfig.length || !data) return;
      
      this.formConfig.forEach(item => {
        if (data[item.key] !== undefined) {
          if (item.type === 'number') {
            if(!data[item.key]){
              data[item.key] = '';
              return;
            }
            // 将数字字符串转换为数字类型
            const numValue = parseFloat(data[item.key]);
            if (!isNaN(numValue)) {
              // 确保数值在有效范围内
              const minValue = this.getMinValue(item);
              const maxValue = this.getMaxValue(item);
              const val = Number(data[item.key]);
              let validValue = '';
              if(val > maxValue){
                validValue = maxValue;
              }else if(val < minValue){
                validValue = minValue;
              }else{
                validValue = val;
              }
              
              // 处理小数位 - 保留小数点
              if (item.decimals !== undefined) {
                data[item.key] = parseFloat(validValue).toFixed(item.decimals);
              } else {
                data[item.key] = validValue;
              }
            } else {
              // 如果无法转换，则使用默认值
              data[item.key] = item.value !== undefined ? parseFloat(item.value) : 0;
            }
          } else if (item.type === 'select') {
            // 确保选择类型的值不是 undefined 或 null
            if (data[item.key] === undefined || data[item.key] === null) {
              data[item.key] = item.value || '';
            }
          }
        } else {
          // 如果值不存在，设置默认值
          if (item.type === 'select') {
            data[item.key] = item.value || '';
          }
        }
      });
    },
    // 从API获取配置
    async fetchConfig() {
      if (!this.configKey || this.loading) return;
      
      this.loading = true;
      try {
        const res = await api.getData(`system-server/config/list?keys=${this.configKey}`);
        if (res.state === 1 && res.data && res.data[this.configKey]) {
          const config = res.data[this.configKey];
          if (config.configValue) {
            try {
              // 尝试解析配置值
              const parsedConfig = JSON.parse(config.configValue);
              this.formConfig = Array.isArray(parsedConfig) ? parsedConfig : [];
              console.log('获取到的表单配置:', this.formConfig);
              this.initFormData();
              
              // 打印初始化后的表单数据
              console.log('初始化后的表单数据:', this.formData);
            } catch (e) {
              console.error('解析配置失败:', e);
              this.formConfig = [];
            }
          } else {
            this.formConfig = [];
          }
        } else {
          this.formConfig = [];
        }
      } catch (error) {
        console.error('获取表单配置失败:', error);
        this.formConfig = [];
      } finally {
        this.loading = false;
      }
    },
    // 初始化表单数据
    initFormData() {
      // 保留现有数据
      const formData = this.formData;
      this.formConfig.forEach(item => {
          if (formData[item.key] === undefined) {
            formData[item.key] = item.type === 'number' ? null : '';
          }
        });  
    },
    // 获取表单数据前进行类型转换
    getData() {
      const data = Object.assign({}, this.formData);
      this.processDataTypes(data);
      return data;
    },
    // 表单验证
    validate() {
      return new Promise((resolve, reject) => {
        // 使用iView的表单校验
        this.$refs.dynamicFormRef.validate(valid => {
          if (valid) {
            resolve(this.getData());
          } else {
            reject(new Error('表单验证失败'));
          }
        });
      });
    },
    // 处理下拉框清除事件
    handleSelectClear(item) {
      // 确保值被正确设置为空
      this.formData[item.key] = '';
    }
  }
}
</script>

<style scoped>
.dynamic-form {
  width: 100%;
}
</style>
